import { Legend, HStack, VStack } from '@genial/components';
import { Color, ColorTypes, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Pressable } from 'native-base';
import React from 'react';

import { ProductTooltip, ProductTooltipTypes } from './components/ProductTooltip';

export declare namespace ProductsListBarTypes {
  interface Product {
    label: string;
    value: number;
    percentage: number;
    percentageFormatted: string;
    color: ColorTypes.Option;
    dataKey: string;
  }
  interface Props {
    products: Product[];
    isMobile?: boolean;
    activeDataKey?: string | null;
    setActiveDataKey: React.Dispatch<React.SetStateAction<string | undefined>>;
    /**@default top */
    tooltipDirection?: ProductTooltipTypes.Props['direction'];
  }
}

export const ProductsListBar = ({
  products,
  isMobile,
  activeDataKey,
  setActiveDataKey,
  tooltipDirection = 'top',
}: ProductsListBarTypes.Props) => {
  const hasActiveDataKey = activeDataKey !== undefined;

  const handlePress = (product: ProductsListBarTypes.Product) => {
    if (product.dataKey === activeDataKey) {
      return setActiveDataKey(undefined);
    }
    setActiveDataKey(product.dataKey);
  };

  const getBackgroundColor = (product: ProductsListBarTypes.Product) => {
    const currentIndexActive = product.dataKey === activeDataKey;
    if (!hasActiveDataKey || currentIndexActive) {
      return Color[product.color];
    }

    return Utils.ColorToken.WithOpacity(product.color, 0.3);
  };

  if (!products?.length) {
    return <Box w="full" h="14px" backgroundColor="#D9D9D9" borderRadius="4px" />;
  }

  return (
    <VStack w="full" space={Spacing['stack.xxs']}>
      <HStack w="full" h="14px" zIndex={2} borderRadius="4px" overflow="hidden">
        {products?.map((product, index) => (
          <Pressable
            key={`product-bar-${index}`}
            position="relative"
            w={`${product.percentage}%`}
            h="full"
            onPress={() => handlePress(product)}>
            {activeDataKey === product.dataKey && (
              <ProductTooltip direction={tooltipDirection} product={product} />
            )}
            <Box w="full" h="full" backgroundColor={getBackgroundColor(product)} />
          </Pressable>
        ))}
      </HStack>
      {!isMobile && (
        <Legend
          testId="legend"
          legend={[
            ...(products?.map(({ color, label, dataKey, percentageFormatted }) => ({
              color,
              hasHover: true,
              label: `${label} (${percentageFormatted})`,
              opaque: hasActiveDataKey && dataKey !== activeDataKey,
              handleClick: () => setActiveDataKey(dataKey === activeDataKey ? undefined : dataKey),
            })) || []),
          ]}
        />
      )}
    </VStack>
  );
};
